/**
 * Curadoria e conteudo editorial da vitrine de campanha da home.
 *
 * Duas responsabilidades, de proposito separadas:
 *
 * 1. QUAIS eventos sobem. A fonte de verdade e o campo `evento_destaque_peso`
 *    do backend, exposto no payload de `/api/events?type=upcoming` como
 *    `highlightWeight` (ou `destaquePeso`). Enquanto esse campo nao existir, a
 *    home usa a lista de ids de `VITE_FEATURED_EVENT_IDS`. A troca e
 *    automatica: no primeiro payload em que qualquer evento vier com peso
 *    maior que zero, a lista manual para de ser considerada.
 *
 * 2. COMO eles aparecem. Nome curto, local, atracoes e selo nao existem na
 *    API. Ficam no manifesto da campanha abaixo, por id de evento.
 *
 * Preco nao e configurado aqui de proposito: lote esgota e o valor muda. O
 * componente so mostra preco quando a API mandar `priceFrom`. Para forcar um
 * valor durante uma campanha curta existe `priceFromOverride` no manifesto,
 * que e mentira assim que o lote virar — use com data para remover.
 */

export const FEATURED_EVENT_IDS_ENV = 'VITE_FEATURED_EVENT_IDS'

/**
 * Campanha vigente ate 31/12/2026. Sobrescreva por env sem tocar no codigo.
 *  1111 - 29/12 Pre Reveillon Cafe de la Musique
 *  1113 - 30/12 Tropical
 *  1114 - Combos 29/12 + 30/12
 */
export const DEFAULT_FEATURED_EVENT_IDS = ['1111', '1113', '1114']

/**
 * Manifesto da campanha. As atracoes e o local vieram da arte oficial e da
 * descricao de cada evento; o nome curto existe porque o nome de cadastro
 * ("COMBOS 29/12 - PRE REVEILLON ... + 30/12 -TROPICAL") nao cabe nem informa.
 */
export const SEMANA_CAFE = {
  slug: 'semana-cafe',
  wordmark: '/campanhas/semana-cafe/marca.jpg',
  wordmarkAlt: 'Semana Café — Café de La Musique Maceió',
  headline: '29 e 30 de dezembro',
  support: '10 anos do café de la musique maceió',
  marks: {
    pre: '/campanhas/semana-cafe/selo-pre.png',
    palmeira: '/campanhas/semana-cafe/selo-palmeira.png',
  },
  events: {
    1111: {
      name: 'Pré-Réveillon',
      dateLabel: '29/12',
      schedule: { label: '29 dez', year: 2026, time: '21h' },
      venue: 'Arena Celebration — Praia de Jacarecica, Maceió',
      lineup: ['Jorge e Mateus', 'Belo', 'Mari Fernandez'],
      mark: 'pre',
      accent: 'coral',
      cta: 'Ver ingressos',
    },
    1113: {
      name: 'Tropical',
      dateLabel: '30/12',
      schedule: { label: '30 dez', year: 2026, time: '17h' },
      venue: 'Café de La Musique Maceió',
      lineup: ['Xand Avião', 'Rey Vaqueiro', 'Toninho'],
      mark: 'palmeira',
      accent: 'mint',
      cta: 'Ver ingressos',
    },
    1114: {
      name: 'Combo · dois dias',
      dateLabel: '29 + 30/12',
      schedule: { label: '29 e 30 dez', year: 2026 },
      venue: 'Arena Celebration + Café de La Musique Maceió',
      lineup: ['Pré-Réveillon + Tropical na mesma compra'],
      mark: 'combo',
      accent: 'amarelo',
      cta: 'Ver combo',
    },
  },
}

export function formatCampaignDate(schedule, currentYear = new Date().getFullYear()) {
  const year = schedule.year === currentYear ? '' : ` ${schedule.year}`
  return `${schedule.label}${year}${schedule.time ? ` · ${schedule.time}` : ''}`
}

const HIGHLIGHT_WEIGHT_KEYS = ['highlightWeight', 'destaquePeso']

const MONTHS_PT = {
  JAN: 0, FEV: 1, MAR: 2, ABR: 3, MAI: 4, JUN: 5,
  JUL: 6, AGO: 7, SET: 8, OUT: 9, NOV: 10, DEZ: 11,
}

/** A partir de quantos dias a contagem regressiva deixa de ser argumento. */
export const COUNTDOWN_MAX_DAYS = 21

/**
 * Le `VITE_FEATURED_EVENT_IDS` no formato "1111,1113,1114".
 * A ordem informada e a ordem de exibicao.
 */
export function parseFeaturedEventIds(rawValue) {
  return String(rawValue ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '')
}

/** Peso de destaque do evento. Zero quando o backend ainda nao envia o campo. */
export function readHighlightWeight(event) {
  for (const key of HIGHLIGHT_WEIGHT_KEYS) {
    const weight = Number(event?.[key])
    if (Number.isFinite(weight) && weight !== 0) {
      return weight
    }
  }

  return 0
}

/** True quando o backend ja assumiu a curadoria. */
export function apiProvidesHighlightWeights(events) {
  return (events ?? []).some((event) => readHighlightWeight(event) > 0)
}

/**
 * Ids em destaque, na ordem de exibicao.
 * Ids que nao existem no payload sao descartados.
 */
export function resolveFeaturedEventIds(events, env = {}) {
  const available = events ?? []

  if (apiProvidesHighlightWeights(available)) {
    return available
      .filter((event) => readHighlightWeight(event) > 0)
      .sort((a, b) => readHighlightWeight(b) - readHighlightWeight(a))
      .map((event) => String(event.eventId))
  }

  const configured = env[FEATURED_EVENT_IDS_ENV] === undefined
    ? DEFAULT_FEATURED_EVENT_IDS
    : parseFeaturedEventIds(env[FEATURED_EVENT_IDS_ENV])

  const existingIds = new Set(available.map((event) => String(event.eventId)))

  return configured.filter((id) => existingIds.has(id))
}

/**
 * Separa os destaques da campanha dos demais eventos disponíveis.
 */
export function splitFeaturedEvents(events, featuredIds) {
  const available = events ?? []
  const wanted = featuredIds ?? []
  const byId = new Map(available.map((event) => [String(event.eventId), event]))

  const featured = wanted
    .map((id) => byId.get(String(id)))
    // Sem manifesto, a vitrine nao renderiza o evento: ele precisa continuar
    // na agenda, mesmo quando o backend manda peso de destaque para outro id.
    .filter((event, index, resolved) => event !== undefined
      && SEMANA_CAFE.events[String(event.eventId)]
      && event.eventUrl
      && resolved.findIndex((item) => item?.eventId === event.eventId) === index)

  const featuredIdSet = new Set(featured.map((event) => String(event.eventId)))
  const rest = available.filter((event) => !featuredIdSet.has(String(event.eventId)))

  return { featured, rest }
}

/**
 * Junta o evento da API com o texto editorial da campanha.
 * Sem entrada no manifesto o evento nao entra na vitrine: melhor ficar de fora
 * do que aparecer com o nome de cadastro em caixa alta no meio da arte.
 */
export function buildCampaignRows(featuredEvents, campaign = SEMANA_CAFE) {
  return (featuredEvents ?? [])
    .map((event) => {
      const editorial = campaign.events?.[String(event.eventId)]
      if (!editorial) {
        return null
      }

      return {
        ...editorial,
        eventId: String(event.eventId),
        eventUrl: event.eventUrl,
        eventName: editorial.name,
        date: formatCampaignDate(editorial.schedule),
        priceFrom: event.priceFrom ?? editorial.priceFromOverride ?? null,
        lotLabel: event.lotLabel ?? editorial.lotLabel ?? null,
      }
    })
    .filter((row) => row !== null)
}

/**
 * Le a data formatada pelo helper `translateData()` do `unique-site`.
 * Formato: "TER, 29 de DEZ de 2026 às 21:00h".
 */
export function parseApiEventDate(dateText) {
  const match = /(\d{1,2})\s+de\s+([A-Za-zÇç]{3})\s+de\s+(\d{4})(?:.*?(\d{1,2}):(\d{2}))?/
    .exec(String(dateText ?? ''))

  if (!match) {
    return null
  }

  const month = MONTHS_PT[match[2].toUpperCase()]
  if (month === undefined) {
    return null
  }

  return new Date(
    Number(match[3]),
    month,
    Number(match[1]),
    Number(match[4] ?? 0),
    Number(match[5] ?? 0),
  )
}

/**
 * Dias inteiros ate o evento. `null` quando a data nao e legivel;
 * zero ou negativo quando o evento e hoje ou ja passou.
 */
export function daysUntilEvent(dateText, now = new Date()) {
  const eventDate = parseApiEventDate(dateText)
  if (eventDate === null) {
    return null
  }

  const startOfEventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate())
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return Math.round((startOfEventDay - startOfToday) / 86400000)
}

/**
 * Selo de urgencia do card.
 *
 * So aparece perto do evento: a mais de tres semanas, "faltam 110 dias" nao
 * aperta ninguem — dá permissao para deixar para depois. Nesse intervalo quem
 * cria urgencia e o lote, nao o calendario.
 */
export function formatCountdownLabel(dateText, now = new Date(), maxDays = COUNTDOWN_MAX_DAYS) {
  const days = daysUntilEvent(dateText, now)

  if (days === null || days < 0 || days > maxDays) {
    return null
  }

  if (days === 0) {
    return 'É hoje'
  }

  if (days === 1) {
    return 'Amanhã'
  }

  return `Faltam ${days} dias`
}
